import { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import { getAccessToken } from "./AccountService";
import config from "../config.json";
import MemberDto from "../Models/MemberDto";
import applicationRole from "../Models/ApplicationRole";
import UserLimitInfo from "../Models/UserLimitInfo";


const getFetchOptions = async (
    instance: IPublicClientApplication,
    account: AccountInfo
): Promise<RequestInit> => {
    const accessToken = await getAccessToken(instance, account);

    let headers = new Headers();
    headers.append("Authorization", `Bearer ${accessToken}`);
    headers.append("Content-Type", "application/json");

    return {
        method: "GET",
        headers,
    };
};
const handleHttpError = async (response: Response): Promise<never> => {
    const error: any = new Error(`HTTP ${response.status}: ${response.statusText}`);
    error.status = response.status;
    error.statusText = response.statusText;

    try {
        const errorData = await response.json();
        error.message = errorData.message || errorData.error || error.message;
    } catch {
        error.message = response.statusText || error.message;
    }

    throw error;
};



// In-flight de-dupe: when an effect re-runs on a quick re-render the same fetch can be
// triggered more than once. Concurrent callers for a given key share one request; the
// entry clears once settled, so a later call still fetches fresh data.
function dedupe<T>(cache: Map<string, Promise<T>>, key: string, work: () => Promise<T>): Promise<T> {
    const existing = cache.get(key);
    if (existing) return existing;

    const request = work().finally(() => cache.delete(key));
    cache.set(key, request);
    return request;
}

const inflightUsers = new Map<string, Promise<MemberDto[]>>();

export async function getUsers(
    instance: IPublicClientApplication,
    account: AccountInfo
): Promise<MemberDto[]> {
    return dedupe(inflightUsers, "all", async () => {
        const options = await getFetchOptions(instance, account);
        const response = await fetch(`${config.ptaasApi.baseUrl}/users`, options);

        if (response.status === 204 || response.status === 200) {
            const users: MemberDto[] = await response.json();

            // Add imageUrl directly from base64 string
            return users.map(user => ({
                ...user,
                imageUrl: `data:image/jpeg;base64,${user.imageData}`
            }));
        }
        if (!response.ok) {
            await handleHttpError(response);
        }
        throw new Error("Failed to fetch users");
    });
}

const inflightProjectUsers = new Map<string, Promise<MemberDto[]>>();

export async function getProjectUsers(
    instance: IPublicClientApplication,
    account: AccountInfo,
    projectid:Number
): Promise<MemberDto[]> {
    return dedupe(inflightProjectUsers, String(projectid), async () => {
        const options = await getFetchOptions(instance, account);
        const response = await fetch(`${config.ptaasApi.baseUrl}/users/${projectid}`, options);
        if (response.status === 204 || response.status === 200) {
            const users: MemberDto[] = await response.json();
            // Add imageUrl directly from base64 string
            return users.map(user => ({
                ...user,
                imageUrl: `data:image/jpeg;base64,${user.imageData}`
            }));
        }
        if (!response.ok) {
            await handleHttpError(response);
        }
        throw new Error("Failed to fetch users");
    });
}


export async function InviteUsers(
    instance: IPublicClientApplication,
    account: AccountInfo,
    displayName: string,
    email: string,
    applicationRoles: applicationRole[],
    accountType: number,
    
): Promise<string> {
    const options = await getFetchOptions(instance, account);
    options.method = "POST";
    options.body = JSON.stringify({ accountType, displayName, email, applicationRoles });
    let response = await fetch(
        `${config.ptaasApi.baseUrl}/users/invite`,
        options
    );
    if (!response.ok) {
        await handleHttpError(response);
    }
    if (response.status === 204 || response.status === 200)
        return await response.text();
    const message = await response.text();

    throw new Error(message || "Failed to invite users");
}
const inflightUserLimit = new Map<string, Promise<UserLimitInfo>>();

export async function getUserLimit(

    instance: IPublicClientApplication,
    account: AccountInfo
): Promise<UserLimitInfo> {
    return dedupe(inflightUserLimit, "self", async () => {
        const options = await getFetchOptions(instance, account);
        const response = await fetch(`${config.ptaasApi.baseUrl}/user-limit`, options);
        if (!response.ok) {
            await handleHttpError(response);
        }
        if (response.status === 204 || response.status === 200)
            return await response.json();
        throw new Error();
    });
}
export async function RevokeAccess(

    instance: IPublicClientApplication,
    account: AccountInfo,
    userid: string
): Promise<boolean> {
    const options = await getFetchOptions(instance, account);
    options.method = "DELETE";

    let response = await fetch(
        `${config.ptaasApi.baseUrl}/users/${userid}`,
        options
    );

    if (response.status === 204 || response.status === 200) {
        return true;
    }

    let message: string = "Failed to revoke user.";
    // Try to extract error message from JSON body if present
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
        try {
            const data = await response.json();
            message = data?.message || data?.error || message;
        } catch {
            // fallback to text below
        }
    } else {
        try {
            const text = await response.text();
            if (text) message = text;
        } catch {
            // ignore
        }
    }

    // Only throw if not ok
    throw new Error(message);
}

export async function UpdateUserDetails(
    instance: IPublicClientApplication,
    account: AccountInfo,
    id: string,
    displayName: string,
    applicationRoles: applicationRole[]
): Promise<boolean> {
    const options = await getFetchOptions(instance, account);
    options.method = "PUT";
    options.body = JSON.stringify({
        "displayName": displayName,
        "applicationRoles": applicationRoles
    });
    let response = await fetch(
        `${config.ptaasApi.baseUrl}/users/${id}/roles`,
        options
    );

    if (response.status === 204 || response.status === 200)
        return true;
    let message: string;
    try {
        const data = await response.json();
        //console.log(data.message);
        message = data?.message || data?.error || "Failed to update user.";
    } catch {
        message = await response.text() || "Failed to update user.";
    }
   
    throw new Error(message);
}
const inflightProjectForSelection = new Map<string, Promise<UserProjectOption[]>>();

export async function GetProjectForSelection(
    instance: IPublicClientApplication,
    account: AccountInfo
): Promise<UserProjectOption[]> {
    return dedupe(inflightProjectForSelection, "all", async () => {
        const options = await getFetchOptions(instance, account);

        const response = await fetch(`${config.ptaasApi.baseUrl}/usersProjectSelection`, options);

        if (response.status === 204 || response.status === 200) {
            const ProjectSelection: UserProjectOption[] = await response.json();
            return ProjectSelection;
        }
        if (!response.ok) {
            await handleHttpError(response);
        }
        throw new Error("Failed to fetch users");
    });
}